import { apiRequest } from "@/services/api/apiClient.js";
import { limparSessao } from "@/services/limparSessao.js";
import { setCorp } from "@/services/api/corporation.js";
import {setMePermissionsManagement} from "@/services/api/permissionManagement.js";
import { useAuthStore } from "@/stores/authStore.js";
import { slugDaAplicacao } from "@/services/sso.js";
import router from "@/router";

/**
 * Troca o ticket de um handoff pelo token desta aplicacao.
 *
 * Sem `Authorization`: nesta origem ainda nao ha sessao nenhuma quando o
 * handoff chega (ou, no caso da personificacao, a sessao que ha e a antiga --
 * o ticket e quem autentica a troca, nao o que estiver em localStorage).
 */
export const trocarTicket = async (ticket) => {
    return apiRequest({
        client: window.API_AUTH,
        method: 'post',
        url: '/sso/exchange',
        body: { ticket },
        sendToken: false,
    });
};

export const loginUser = async (credentials) => {
    const authStore = useAuthStore();

    try {
        const slug = slugDaAplicacao(window.appConfig);

        const response = await apiRequest({
            client: window.API_AUTH,
            method: "post",
            url: "/login",
            // `app` diz ao auth qual aplicacao esta entrando: e o que faz o
            // token sair com a audiencia e com a lista de APIs do registry.
            // Opcional do lado do servidor -- auth antigo simplesmente ignora.
            body: slug ? { ...credentials, app: slug } : credentials,
            sendToken: false,
        });

        const deviceToken = response?.credentials?.device_token;
        if (deviceToken) {
            setDeviceToken(deviceToken);
        }

        const userCorps = response?.credentials?.user?.corps ?? {};
        const firstCorp = Object.values(userCorps)[0];

        await setAuth({
            token: response.access_token,
            credentials: response.credentials,
        });

        authStore.scheduleTokenRefresh();
        await authStore.fetchPermissions();

        if (Object.keys(userCorps).length === 1) {
            await setCorp({
                id: firstCorp.id,
                advancedCredentials:
                    (response?.credentials?.permissions?.admin?.[0] === "*" &&
                        response?.credentials?.permissions?.allow?.[0] === "*")
                        ? response?.credentials
                        : null
            });
        }
        return response;

    } catch (error) {
        if (error.status === 428) {
            await setSecretKey({
                secretKey: error.response?.data?.secret_2fa,
                svg2fa: error.response?.data?.svg_2fa,
            });
        }
        return Promise.reject(error);
    }
};

export const setDeviceToken = (deviceToken) => {
    localStorage.setItem("device_token", deviceToken);
};

export const getDeviceToken = () => {
    return localStorage.getItem("device_token") || "";
};

export const updateUser = async (credentials) => {
    const idUserLogged = getUser()?.id;

    const response = await apiRequest({
        client: window.API_AUTH,
        method: "put",
        url: `/user/${credentials.id}`,
        body: {
            name: credentials.name,
            email: credentials.email,
        },
    });

    if (idUserLogged === credentials.id) {
        await setUser();
    }

    return response;
};

export const setUser = async () => {
    const authStore = useAuthStore();

    const response = await apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: "/me",
    });

    setAuth({
        token: null,
        credentials: { user: response.data },
    });

    authStore.scheduleTokenRefresh();
    await authStore.fetchPermissions();

    return response;
};

export const resetPassword = async (credentials) => {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: "/user/resetPassword",
        body: credentials,
        sendToken: false,
    });
};

export const changeUserPassword = async (data) => {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: `/user/${data.id}/changePassword`,
        body: {
            oldpassword: data.current_password,
            newpassword: data.new_password,
        },
    });
};

export const forgotPassword = async (login) => {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: "/user/sendPasswordAuthorizationKey",
        body: { login: login.value },
        sendToken: false,
    });
};

export const createAccount = async () => {
    return {
        available: false,
    };
};

export const createGoogleAccount = async () => {
    return {
        available: false,
    };
};

const getCorporationServerName = () => {
    const apiAuth = (window.API_AUTH?.defaults?.baseURL || '').toLowerCase();

    if (apiAuth.includes('localhost') || apiAuth.includes('127.0.0.1')) {
        return 'local';
    }

    if (apiAuth.includes('apidev')) {
        return 'dbdev01';
    }

    if (apiAuth.includes('api')) {
        return 'dbprod01';
    }

    return 'local';
};

export const createCorporation = async ({ authName, companyName, businessName, imageContent }) => {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: "/corp",
        body: {
            auth_name: authName,
            company_name: companyName,
            business_name: businessName,
            image_content: imageContent,
            server_name: getCorporationServerName(),
        },
    });
};

export const setAuth = ({ token, credentials }) => {
    try {
        if (token) {
            localStorage.setItem('authToken', token);

            const payloadBase64 = token.split('.')[1];
            const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');

            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );

            const payload = JSON.parse(jsonPayload);
            localStorage.setItem('tokenExp', payload.exp);
        }

        if (credentials) {
            localStorage.setItem('credentials', JSON.stringify(credentials));
        }

    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const hasSession = () => {
    return !!localStorage.getItem('authToken');
};

export const tokenNeedsRefresh = () => {
    const exp = localStorage.getItem('tokenExp');
    if (!exp) return true;

    const now = Date.now();
    const expTime = Number(exp) * 1000;

    return now >= (expTime - (5 * 60 * 1000));
};

export const getToken = () => {
    return localStorage.getItem('authToken');
};

export const getUser = () => {
    const stored = localStorage.getItem('credentials');
    if (!stored) return null;

    const parsedWrapper = JSON.parse(stored);
    const parsed = parsedWrapper.user ?? parsedWrapper;

    const fullname = (parsed.name || '').trim();
    const parts = fullname.split(/\s+/).filter(Boolean);

    const name = parts[0] || '';
    const lastName = parts.slice(1).join(' ');

    const corpId = parsedWrapper.corp ?? '';
    const corps = parsed.corps ?? {};

    let workgroups = [];

    for (const corpData of Object.values(corps)) {
        if (corpData.id === corpId) {
            const wg = corpData.workgroups ?? {};
            workgroups = Object.entries(wg).map(([name, id]) => ({ name, id }));
            break;
        }
    }

    return {
        id: parsed.id,
        name,
        fullname,
        lastName,
        username: parsed.login,
        email: parsed.email,
        corpId,
        workgroups,
        fullUser: parsedWrapper
    };
};

export const setSecretKey = ({ secretKey, svg2fa }) => {
    localStorage.setItem('secretKey', secretKey);
    localStorage.setItem('svg2fa', svg2fa);
};

export const getSecretKey = () => ({
    secretKey: localStorage.getItem('secretKey'),
    svg2fa: localStorage.getItem('svg2fa')
});

export const removeSecretKey = () => {
    localStorage.removeItem('secretKey');
    localStorage.removeItem('svg2fa');
};

export const logout = async ({ reason } = {}) => {
    const authStore = useAuthStore();

    try {
        await apiRequest({
            client: window.API_AUTH,
            method: "delete",
            url: "/logout",
            sendToken: true,
        });
    } catch (error) {
        console.error(error);
    }

    if (reason === 'expired') {
        message.error(t('common.messages.errors.sessionExpired'));
    }

    limparSessao();

    authStore.$reset();
};

export const refreshToken = async () => {
    const authStore = useAuthStore();

    try {
        const token = getToken();

        if (token) {
            const response = await apiRequest({
                client: window.API_AUTH,
                method: "get",
                url: "/refresh",
                sendToken: true,
            });

            setAuth({
                token: response.access_token,
                credentials: response.credentials
            });

            authStore.scheduleTokenRefresh();
        }

    } catch (error) {
        limparSessao();

        message.error(t('common.messages.errors.sessionExpired'));
        router.push('/');
    }
};
